import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PUBLIC_API_BASE, storagePublicUrl } from '../../utils/partnerPublicDisplay';
import '../external/external.css';

/* ── SVG Social Icons ── */
const EmailIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <polyline points="2,4 12,13 22,4"/>
  </svg>
);
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
  </svg>
);
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none"/>
  </svg>
);
const YoutubeIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 8s-.3-2-1.2-2.8c-1.1-1.2-2.4-1.2-3-1.3C15.6 3.8 12 3.8 12 3.8s-3.6 0-5.8.1c-.6.1-1.9.1-3 1.3C2.3 6 2 8 2 8S1.8 10.3 1.8 12.6v2.1C1.8 17 2 19 2 19s.3 2 1.2 2.8c1.1 1.2 2.6 1.1 3.3 1.2C8.8 23.2 12 23.2 12 23.2s3.6 0 5.8-.2c.6-.1 1.9-.1 3-1.3.9-.8 1.2-2.8 1.2-2.8s.2-2.3.2-4.6v-2.1C22.2 10.3 22 8 22 8z"/>
    <polygon points="10,9 15,12 10,15" fill="currentColor" stroke="none"/>
  </svg>
);
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);
const DiscordIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.3 4.4A19.2 19.2 0 0015.6 3c-.2.4-.5.9-.6 1.3a17.8 17.8 0 00-5.9 0C9 3.9 8.6 3.4 8.4 3A19.4 19.4 0 003.7 4.4C.5 9.3-.3 14 .1 18.7a19.4 19.4 0 005.9 3c.5-.7.9-1.4 1.3-2.2a12.6 12.6 0 01-2-.9c.2-.1.3-.2.5-.4a13.8 13.8 0 0011.8 0c.2.1.3.3.5.4-.7.4-1.3.7-2 1 .4.8.8 1.5 1.2 2.2a19.3 19.3 0 005.9-3c.5-5.4-.9-10-3.9-14.3zM8.5 15.7c-1.3 0-2.3-1.2-2.3-2.6s1-2.6 2.3-2.6 2.3 1.2 2.3 2.6-1 2.6-2.3 2.6zm7 0c-1.3 0-2.3-1.2-2.3-2.6s1-2.6 2.3-2.6 2.3 1.2 2.3 2.6-1 2.6-2.3 2.6z"/>
  </svg>
);

const CLUB_LOGO_SRC = '/picture/LogoClub/UTMRBCLOGO.png';

const socialIconMap = {
  'Email': <EmailIcon />,
  'Facebook': <FacebookIcon />,
  'Instagram': <InstagramIcon />,
  'Youtube': <YoutubeIcon />,
  'LinkedIn': <LinkedInIcon />,
  'Discord': <DiscordIcon />,
};

/* ── Particle background (lightweight) ── */
const ParticleBg = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    const pts = Array.from({ length: 40 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.2 + 0.4,
      a: Math.random() * 0.4 + 0.08,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,68,68,${p.a})`;
        ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 130) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(255,68,68,${0.06 * (1 - d / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.7 }}
    />
  );
};

/* ── Main Layout ── */
const ExternalLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [tooltipIdx, setTooltipIdx] = useState(null);
  const [footerPartnerLogos, setFooterPartnerLogos] = useState([]);

  const activeRoute = location.pathname;

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const loadFooterPartnerLogos = async () => {
      try {
        const response = await fetch(`${PUBLIC_API_BASE}/partners/public`);
        if (!response.ok) return;
        const payload = await response.json();
        const partners = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
        const logos = [];
        partners.forEach((partner) => {
          const logoPath = partner?.company?.company_logo;
          const companyName = partner?.company?.name;
          if (!logoPath || !companyName) return;
          const src = storagePublicUrl(logoPath);
          if (!src) return;
          logos.push({
            id: `${partner.id}-${companyName}`,
            src,
            name: companyName,
          });
        });
        const uniqueLogos = logos.filter(
          (logo, idx, arr) => idx === arr.findIndex((item) => item.src === logo.src)
        );
        setFooterPartnerLogos(uniqueLogos.slice(0, 12));
      } catch {
        setFooterPartnerLogos([]);
      }
    };

    loadFooterPartnerLogos();
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const pages = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Activities', path: '/activities' },
    { name: 'Achievements', path: '/achievements' },
    { name: 'Sponsors', path: '/partners/public' },
    { name: 'Contact', path: '/contact' },
  ];

  const socialLinks = [
    { icon: 'Email', link: 'mailto:utmroboconsecretary@gmail.com', label: 'Email' },
    { icon: 'Facebook', link: 'https://www.facebook.com/utmrbc', label: 'Facebook' },
    { icon: 'Instagram', link: 'https://www.instagram.com/utm.rbc/', label: 'Instagram' },
    { icon: 'Youtube', link: 'https://www.youtube.com/@utmrobocon1445', label: 'Youtube' },
    { icon: 'LinkedIn', link: 'https://www.linkedin.com/company/utmrbc/about/', label: 'LinkedIn' },
    { icon: 'Discord', link: 'https://discord.gg/vFTe2rEqq8', label: 'Discord' },
  ];

  const handleNavClick = (path) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  const accountPath = user ? '/dashboard' : '/login';
  const accountLabel = user ? 'DASHBOARD' : 'LOGIN';

  return (
    <div className="el">
      <ParticleBg />

      {/* Grid overlay */}
      <div className="grid-overlay" aria-hidden="true" />
      <div className="scanlines" aria-hidden="true" />

      {/* ── NAVBAR ── */}
      <header className={`navbar${isScrolled ? ' scrolled' : ''}`}>
        <div className="nav-inner">

          {/* Logo */}
          <button type="button" className="logo-btn" onClick={() => handleNavClick('/')}>
            <img src={CLUB_LOGO_SRC} alt="UTM Robocon" className="logo-img" />
            <span className="logo-text">UTM Robocon</span>
          </button>

          {/* Desktop nav */}
          <nav className="desk-nav" aria-label="Main navigation">
            {pages.map(p => (
              <button
                key={p.name}
                className={`nav-link${activeRoute === p.path ? ' active' : ''}`}
                onClick={() => handleNavClick(p.path)}
              >
                {p.name}
                <span className="nav-underline" />
              </button>
            ))}
          </nav>

          {/* Login / return to the authenticated internal area */}
          <button className="login-btn desk-only" onClick={() => handleNavClick(accountPath)}>
            <span className="login-shine" />
            {accountLabel}
          </button>

          {/* Hamburger */}
          <button
            className="ham-btn"
            onClick={() => setIsMenuOpen(o => !o)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            <span className={`ham${isMenuOpen ? ' open' : ''}`}>
              <span /><span /><span />
            </span>
          </button>
        </div>

        {/* Mobile Nav */}
        <div className={`mob-nav${isMenuOpen ? ' open' : ''}`} aria-hidden={!isMenuOpen}>
          <div className="mob-nav-inner">
            <div className="mob-sys-tag">
              <span className="mob-dot" /><span>// NAVIGATION_MODULE</span>
            </div>
            {pages.map((p, i) => (
              <button
                key={p.name}
                className={`mob-link${activeRoute === p.path ? ' active' : ''}`}
                onClick={() => handleNavClick(p.path)}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <span className="mob-idx">{String(i + 1).padStart(2, '0')}</span>
                {p.name}
                {activeRoute === p.path && <span className="mob-active-dot" />}
              </button>
            ))}
            <div className="mob-divider" />
            <button className="mob-login" onClick={() => handleNavClick(accountPath)}>
              <span className="mob-login-pulse" />
              {accountLabel}
            </button>
          </div>
        </div>
      </header>

      {/* ── MAIN ── */}
      <main className="main-area">
        <Outlet />
      </main>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-sweep" aria-hidden="true" />
        <div className="footer-inner">
          <div className="footer-grid">

            {/* Brand */}
            <div className="footer-col">
              <div className="footer-logo">
                <img src={CLUB_LOGO_SRC} alt="UTM Robocon" className="footer-logo-img" />
              </div>
              <p className="footer-tagline">Building the Future of Robotics</p>
              <div className="footer-status">
                <span className="f-status-dot" />
                <span className="f-status-text">SYSTEM ONLINE</span>
              </div>
              <p className="footer-copy">© {new Date().getFullYear()} UTM Robocon Team. All rights reserved.</p>
            </div>

            {/* Social */}
            <div className="footer-col">
              <div className="footer-sec-header">
                <span className="footer-sec-tag">// CONNECT</span>
                <h3 className="footer-sec-title">FIND US</h3>
              </div>
              <div className="social-grid">
                {socialLinks.map((s, i) => (
                  <a
                    key={i}
                    href={s.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-btn"
                    aria-label={s.label}
                    onMouseEnter={() => setTooltipIdx(i)}
                    onMouseLeave={() => setTooltipIdx(null)}
                  >
                    <span className="social-icon">{socialIconMap[s.icon]}</span>
                    <span className="social-label">{s.label}</span>
                    {tooltipIdx === i && (
                      <span className="social-tooltip">{s.label}</span>
                    )}
                    <span className="social-glow-line" />
                  </a>
                ))}
              </div>
            </div>

          </div>

          {footerPartnerLogos.length > 0 && (
            <div className="footer-partners">
              <div className="footer-sec-header footer-partner-header">
                <span className="footer-sec-tag">// PARTNERS</span>
                <h3 className="footer-sec-title">PUBLIC PARTNERS</h3>
              </div>
              <div className="footer-partner-logos" aria-label="Public partner logos">
                {footerPartnerLogos.map((logo) => (
                  <img
                    key={logo.id}
                    src={logo.src}
                    alt={logo.name}
                    className="footer-partner-logo"
                    loading="lazy"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Bottom bar */}
          <div className="footer-bar">
            <div className="footer-bar-line" />
            <span className="footer-bar-text">UTM_ROBOCON // EST. 2002 // MALAYSIA</span>
            <div className="footer-bar-line footer-bar-line-r" />
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&family=Rajdhani:wght@300;400;500;600;700&family=Share+Tech+Mono&display=swap');

        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

        .el {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          background: #060608;
          color: #e0e0e0;
          font-family: 'Rajdhani', -apple-system, BlinkMacSystemFont, sans-serif;
          position: relative;
        }

        /* ── Overlays ── */
        .grid-overlay {
          position: fixed; inset: 0; z-index: 1; pointer-events: none;
          background-image:
            linear-gradient(rgba(255,68,68,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,68,68,0.035) 1px, transparent 1px);
          background-size: 80px 80px;
        }
        .scanlines {
          position: fixed; inset: 0; z-index: 2; pointer-events: none;
          background: repeating-linear-gradient(
            0deg, transparent, transparent 2px,
            rgba(0,0,0,0.055) 2px, rgba(0,0,0,0.055) 4px
          );
        }

        /* ── NAVBAR ── */
        .navbar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          background: rgba(6,6,10,0.88);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,68,68,0.12);
          transition: box-shadow 0.4s ease, background 0.4s ease;
        }
        .navbar.scrolled {
          background: rgba(6,6,10,0.97);
          box-shadow: 0 4px 32px rgba(255,34,34,0.15), 0 0 1px rgba(255,68,68,0.4);
        }
        .navbar::after {
          content: '';
          position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,68,68,0.5), transparent);
          opacity: 0; transition: opacity 0.4s;
        }
        .navbar.scrolled::after { opacity: 1; }

        .nav-inner {
          max-width: 1380px; margin: 0 auto;
          padding: 0 2rem;
          display: flex; align-items: center; justify-content: space-between;
          height: 76px;
        }

        /* Logo */
        .logo-btn {
          display: flex; align-items: center; gap: 10px;
          background: none; border: none; cursor: pointer;
          padding: 0;
        }
        .logo-img {
          height: 40px; width: auto; max-width: 200px;
          object-fit: contain; display: block;
          transition: filter 0.3s;
        }
        .logo-text{
          font-family: 'Orbitron', sans-serif;
          font-weight: 800;
          letter-spacing: 3px;
          text-transform: uppercase;
          font-size: 0.85rem;
          color: #e8edf5;
          line-height: 1;
          white-space: nowrap;
          opacity: 0.92;
          transition: color 0.25s ease, text-shadow 0.3s ease, opacity 0.25s ease;
        }
        .logo-btn:hover .logo-img {
          filter: drop-shadow(0 0 12px rgba(255,68,68,0.55));
        }
        .logo-btn:hover .logo-text{
          color: #fff;
          opacity: 1;
          text-shadow: 0 0 14px rgba(255,68,68,0.28);
        }
        @keyframes logo-blink{0%,100%{opacity:1;box-shadow:0 0 8px #ff4444}50%{opacity:0.5;box-shadow:0 0 3px #ff4444}}

        /* Desktop nav */
        .desk-nav {
          display: flex; align-items: center; gap: 0.2rem;
        }
        .nav-link {
          background: none; border: none; cursor: pointer;
          font-family: 'Rajdhani', sans-serif;
          font-size: 0.95rem; font-weight: 600; letter-spacing: 1px;
          color: #b0b8c4; padding: 0.5rem 1rem;
          position: relative; transition: color 0.25s;
          text-transform: uppercase;
        }
        .nav-link:hover { color: #fff; }
        .nav-link.active { color: #ff4444; }
        .nav-underline {
          position: absolute; bottom: 0; left: 50%; right: 50%;
          height: 1px; background: #ff4444;
          box-shadow: 0 0 6px #ff4444;
          transition: left 0.3s ease, right 0.3s ease, box-shadow 0.3s;
        }
        .nav-link:hover .nav-underline,
        .nav-link.active .nav-underline {
          left: 10%; right: 10%;
        }
        .nav-link.active .nav-underline {
          box-shadow: 0 0 10px #ff4444;
        }

        /* Login button */
        .login-btn {
          position: relative; overflow: hidden;
          background: transparent;
          border: 1px solid rgba(255,68,68,0.45);
          color: #ff4444; border-radius: 3px;
          padding: 0.55rem 1.4rem;
          font-family: 'Orbitron', sans-serif;
          font-size: 0.7rem; font-weight: 700; letter-spacing: 2.5px;
          cursor: pointer; transition: all 0.3s ease;
        }
        .login-btn:hover {
          background: rgba(255,68,68,0.1);
          border-color: #ff4444;
          color: #fff;
          box-shadow: 0 0 18px rgba(255,68,68,0.25);
        }
        .login-shine {
          position: absolute; top: 0; left: -80%; width: 50%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          transition: left 0.5s ease;
        }
        .login-btn:hover .login-shine { left: 130%; }
        .desk-only { display: flex; }

        /* Hamburger */
        .ham-btn {
          display: none; background: none; border: none; cursor: pointer;
          padding: 8px; border-radius: 4px;
          transition: background 0.2s;
        }
        .ham-btn:hover { background: rgba(255,68,68,0.08); }
        .ham {
          display: flex; flex-direction: column;
          justify-content: space-between;
          width: 22px; height: 16px;
        }
        .ham span {
          display: block; height: 1.5px; width: 100%;
          background: #ff4444;
          border-radius: 2px;
          transition: all 0.3s ease;
          transform-origin: center;
        }
        .ham.open span:nth-child(1) { transform: translateY(7.25px) rotate(45deg); }
        .ham.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
        .ham.open span:nth-child(3) { transform: translateY(-7.25px) rotate(-45deg); }

        /* ── Mobile Nav ── */
        .mob-nav {
          position: absolute; top: 100%; left: 0; right: 0;
          background: rgba(6,6,10,0.98);
          backdrop-filter: blur(24px);
          border-bottom: 1px solid rgba(255,68,68,0.15);
          max-height: 0; overflow: hidden;
          transition: max-height 0.4s ease, opacity 0.35s ease;
          opacity: 0;
        }
        .mob-nav.open { max-height: 600px; opacity: 1; }

        .mob-nav-inner {
          padding: 1.5rem 1.8rem 2rem;
          display: flex; flex-direction: column; gap: 0.3rem;
        }
        .mob-sys-tag {
          display: flex; align-items: center; gap: 8px;
          font-family: 'Share Tech Mono', monospace;
          font-size: 0.6rem; color: #555; letter-spacing: 1.5px;
          margin-bottom: 1rem;
        }
        .mob-dot {
          width: 6px; height: 6px; border-radius: 50%; background: #ff4444;
          box-shadow: 0 0 6px #ff4444;
          animation: logo-blink 1.5s infinite;
        }

        .mob-link {
          background: none; border: none; cursor: pointer;
          text-align: left;
          display: flex; align-items: center; gap: 12px;
          font-family: 'Rajdhani', sans-serif;
          font-size: 1.05rem; font-weight: 600; letter-spacing: 1px;
          color: #8a97a8; text-transform: uppercase;
          padding: 0.85rem 1rem;
          border-radius: 3px; border: 1px solid transparent;
          transition: all 0.25s ease;
          position: relative;
        }
        .mob-link:hover {
          color: #fff; background: rgba(255,68,68,0.06);
          border-color: rgba(255,68,68,0.15);
        }
        .mob-link.active {
          color: #ff4444;
          background: rgba(255,68,68,0.08);
          border-color: rgba(255,68,68,0.3);
          box-shadow: inset 0 0 16px rgba(255,68,68,0.05),
                      0 0 12px rgba(255,68,68,0.08);
          text-shadow: 0 0 10px rgba(255,68,68,0.5);
        }
        .mob-idx {
          font-family: 'Share Tech Mono', monospace;
          font-size: 0.6rem; color: rgba(255,68,68,0.5); letter-spacing: 1px;
          flex-shrink: 0;
        }
        .mob-active-dot {
          position: absolute; right: 1rem; top: 50%;
          transform: translateY(-50%);
          width: 5px; height: 5px; border-radius: 50%;
          background: #ff4444; box-shadow: 0 0 6px #ff4444;
        }

        .mob-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,68,68,0.2), transparent);
          margin: 0.8rem 0;
        }

        .mob-login {
          position: relative; overflow: hidden;
          background: rgba(255,34,34,0.12);
          border: 1px solid rgba(255,68,68,0.35); border-radius: 3px;
          color: #ff4444; cursor: pointer;
          font-family: 'Orbitron', sans-serif;
          font-size: 0.72rem; font-weight: 700; letter-spacing: 3px;
          padding: 1rem; text-align: center;
          animation: mob-login-pulse 2.5s ease-in-out infinite;
          transition: background 0.3s, color 0.3s;
        }
        .mob-login:hover {
          background: rgba(255,68,68,0.2); color: #fff;
        }
        @keyframes mob-login-pulse {
          0%,100%{box-shadow:0 0 10px rgba(255,34,34,0.2)}
          50%{box-shadow:0 0 22px rgba(255,34,34,0.45)}
        }
        .mob-login-pulse {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(255,68,68,0.08), transparent);
          animation: grad-shift 3s linear infinite;
          background-size: 200% 100%;
        }
        @keyframes grad-shift{0%{background-position:0%}100%{background-position:200%}}

        /* ── MAIN ── */
        .main-area {
          flex: 1;
          margin-top: 76px;
          position: relative;
        }

        /* ── FOOTER ── */
        .footer {
          position: relative; z-index: 3;
          background: rgba(8,8,14,0.98);
          border-top: 1px solid rgba(255,68,68,0.12);
          overflow: hidden;
        }
        .footer-sweep {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse at 10% 50%, rgba(255,34,34,0.06), transparent 60%),
                      radial-gradient(ellipse at 90% 50%, rgba(255,34,34,0.04), transparent 60%);
          animation: footer-sweep 8s ease-in-out infinite alternate;
          pointer-events: none;
        }
        @keyframes footer-sweep{
          from{opacity:0.7}to{opacity:1}
        }

        .footer-inner {
          position: relative; z-index: 1;
          max-width: 1380px; margin: 0 auto; padding: 3.5rem 2rem 2rem;
        }
        .footer-grid {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 4rem; margin-bottom: 2.5rem;
        }

        /* Footer brand */
        .footer-logo {
          display: flex; align-items: center; gap: 10px; margin-bottom: 0.8rem;
        }
        .footer-logo-img {
          height: 36px; width: auto; max-width: 200px;
          object-fit: contain; display: block;
        }
        .footer-tagline {
          font-size: 0.95rem; color: #7a8a9a; margin-bottom: 1rem; font-weight: 500;
        }
        .footer-status {
          display: flex; align-items: center; gap: 8px; margin-bottom: 1rem;
        }
        .f-status-dot {
          width: 6px; height: 6px; border-radius: 50%; background: #44ff88;
          box-shadow: 0 0 8px #44ff88; animation: logo-blink 1.8s infinite;
        }
        .f-status-text {
          font-family: 'Share Tech Mono', monospace;
          font-size: 0.6rem; color: #44ff88; letter-spacing: 2px;
        }
        .footer-copy {
          font-family: 'Share Tech Mono', monospace;
          font-size: 0.6rem; color: #444; letter-spacing: 0.5px; line-height: 1.6;
        }

        /* Footer social */
        .footer-sec-header { margin-bottom: 1.4rem; }
        .footer-sec-tag {
          display: block;
          font-family: 'Share Tech Mono', monospace;
          font-size: 0.6rem; color: #555; letter-spacing: 1.5px; margin-bottom: 4px;
        }
        .footer-sec-title {
          font-family: 'Orbitron', sans-serif;
          font-size: 0.85rem; font-weight: 700; color: #fff; letter-spacing: 3px;
        }

        .social-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.8rem;
        }
        .social-btn {
          position: relative; overflow: hidden;
          display: flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,68,68,0.1);
          border-radius: 3px; padding: 0.75rem 0.9rem;
          color: #7a8a9a; text-decoration: none;
          font-family: 'Rajdhani', sans-serif;
          font-size: 0.85rem; font-weight: 600; letter-spacing: 0.5px;
          transition: all 0.25s ease;
        }
        .social-btn:hover {
          color: #fff;
          background: rgba(255,68,68,0.08);
          border-color: rgba(255,68,68,0.4);
          transform: translateY(-2px);
          box-shadow: 0 4px 18px rgba(255,68,68,0.12);
        }
        .social-icon {
          display: flex; align-items: center; flex-shrink: 0;
          transition: filter 0.25s;
        }
        .social-btn:hover .social-icon {
          filter: drop-shadow(0 0 4px rgba(255,68,68,0.8));
          color: #ff4444;
        }
        .social-label { flex: 1; }
        .social-glow-line {
          position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg, transparent, #ff4444, transparent);
          opacity: 0; transition: opacity 0.3s;
        }
        .social-btn:hover .social-glow-line { opacity: 1; }
        .social-tooltip {
          position: absolute; bottom: calc(100% + 6px); left: 50%;
          transform: translateX(-50%);
          background: rgba(6,6,10,0.96);
          border: 1px solid rgba(255,68,68,0.3);
          color: #ff6644;
          font-family: 'Share Tech Mono', monospace;
          font-size: 0.58rem; letter-spacing: 1px; white-space: nowrap;
          padding: 4px 10px; border-radius: 2px;
          pointer-events: none; z-index: 10;
          box-shadow: 0 0 12px rgba(255,68,68,0.2);
        }

        .footer-partners {
          margin-bottom: 2.2rem;
        }
        .footer-partner-header {
          margin-bottom: 1rem;
        }
        .footer-partner-logos {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
          gap: 0.8rem;
        }
        .footer-partner-logo {
          width: 100%;
          height: 58px;
          object-fit: contain;
          padding: 0.4rem 0.6rem;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,68,68,0.12);
          border-radius: 3px;
          transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
        }
        .footer-partner-logo:hover {
          border-color: rgba(255,68,68,0.35);
          box-shadow: 0 0 14px rgba(255,68,68,0.15);
          transform: translateY(-1px);
        }

        /* Footer bar */
        .footer-bar {
          display: flex; align-items: center; gap: 1rem;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(255,68,68,0.08);
        }
        .footer-bar-line {
          flex: 1; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,68,68,0.25));
        }
        .footer-bar-line-r {
          background: linear-gradient(90deg, rgba(255,68,68,0.25), transparent);
        }
        .footer-bar-text {
          font-family: 'Share Tech Mono', monospace;
          font-size: 0.58rem; color: #3a4050; letter-spacing: 2px; white-space: nowrap;
        }

        /* ── Responsive ── */
        @media(max-width:1024px){
          .desk-nav { display: none; }
          .desk-only { display: none; }
          .ham-btn { display: flex; }
        }
        @media(max-width:768px){
          .nav-inner { padding: 0 1.2rem; height: 68px; }
          .main-area { margin-top: 68px; }
          .footer-grid { grid-template-columns: 1fr; gap: 2.5rem; }
          .social-grid { grid-template-columns: repeat(2, 1fr); }
          .footer-inner { padding: 2.5rem 1.5rem 1.5rem; }
          .footer-partner-logo { height: 52px; }
        }
        @media(max-width:480px){
          .logo-img { height: 34px; max-width: 160px; }
          .logo-text { font-size: 0.75rem; letter-spacing: 2.5px; }
          .social-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default ExternalLayout;
